import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Brain, Loader2, TrendingUp, AlertCircle, Lightbulb } from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import { Badge } from "@/components/ui/badge";

export default function PredictiveAnalysis({ investments, investors }) {
  const [analysis, setAnalysis] = useState(null);

  const analyzeMutation = useMutation({
    mutationFn: async () => {
      // Prepare data for LLM
      const investmentSummary = investments.map(inv => ({
        amount: inv.investment_amount,
        date: inv.investment_date,
        status: inv.status,
        exit_amount: inv.exit_amount,
        company: inv.company_name,
      }));

      const investorSummary = investors.map(inv => ({
        type: inv.type,
        status: inv.status,
        deal_stage: inv.deal_stage,
      }));

      const result = await base44.integrations.Core.InvokeLLM({
        prompt: `Du er en finansiell analytiker. Analyser følgende investeringsdata og gi prediktive innsikter:

Investeringer: ${JSON.stringify(investmentSummary)}
Investorer: ${JSON.stringify(investorSummary)}

Basert på historiske data, gi:
1. Trender i investeringsatferd
2. Prediksjoner for fremtidige investeringsmuligheter
3. Risikovurdering
4. Anbefalinger for porteføljeoptimalisering

Gi et strukturert JSON-svar med følgende felter:
- trends: array av trender (string)
- predictions: array av prediksjoner (string)
- risk_assessment: object med risk_level (low/medium/high) og risk_factors (array)
- recommendations: array av anbefalinger (string)
- market_insights: array av markedsinnsikter (string)
- confidence_score: number (0-100)`,
        add_context_from_internet: true,
        response_json_schema: {
          type: "object",
          properties: {
            trends: { type: "array", items: { type: "string" } },
            predictions: { type: "array", items: { type: "string" } },
            risk_assessment: {
              type: "object",
              properties: {
                risk_level: { type: "string", enum: ["low", "medium", "high"] },
                risk_factors: { type: "array", items: { type: "string" } }
              }
            },
            recommendations: { type: "array", items: { type: "string" } },
            market_insights: { type: "array", items: { type: "string" } },
            confidence_score: { type: "number" }
          }
        }
      });

      return result;
    },
    onSuccess: (data) => {
      setAnalysis(data);
    },
  });

  const riskConfig = {
    low: { color: "text-emerald-600", bgColor: "bg-emerald-50", borderColor: "border-emerald-200", label: "Lav risiko" },
    medium: { color: "text-amber-600", bgColor: "bg-amber-50", borderColor: "border-amber-200", label: "Middels risiko" },
    high: { color: "text-red-600", bgColor: "bg-red-50", borderColor: "border-red-200", label: "Høy risiko" },
  };

  const currentRisk = analysis?.risk_assessment?.risk_level || "medium";
  const config = riskConfig[currentRisk];

  return (
    <Card className="p-6 bg-white border-0 shadow-sm">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-lime-100 flex items-center justify-center">
            <Brain className="w-5 h-5 text-zinc-900" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Prediktiv Analyse</h3>
            <p className="text-sm text-slate-500">AI-drevne innsikter og prognoser</p>
          </div>
        </div>
        <Button
          onClick={() => analyzeMutation.mutate()}
          disabled={analyzeMutation.isPending || investments.length === 0}
          size="sm"
        >
          {analyzeMutation.isPending ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Analyserer...
            </>
          ) : (
            <>
              <Brain className="w-4 h-4 mr-2" />
              Generer Analyse
            </>
          )}
        </Button>
      </div>

      {investments.length === 0 && (
        <p className="text-sm text-slate-500 text-center py-8">
          Ingen investeringsdata å analysere ennå
        </p>
      )}

      {analysis && (
        <div className="space-y-6">
          {/* Confidence Score */}
          {analysis.confidence_score && (
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <span className="text-sm font-medium text-slate-700">Pålitelighetsscore</span>
              <div className="flex items-center gap-2">
                <div className="w-32 h-2 bg-slate-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-zinc-900 rounded-full transition-all"
                    style={{ width: `${analysis.confidence_score}%` }}
                  />
                </div>
                <Badge variant="secondary">{analysis.confidence_score}%</Badge>
              </div>
            </div>
          )}

          {/* Risk Assessment */}
          {analysis.risk_assessment && (
            <div className={`p-4 rounded-xl border ${config.bgColor} ${config.borderColor}`}>
              <div className="flex items-center gap-2 mb-3">
                <AlertCircle className={`w-5 h-5 ${config.color}`} />
                <h4 className={`font-semibold ${config.color}`}>{config.label}</h4>
              </div>
              {analysis.risk_assessment.risk_factors && analysis.risk_assessment.risk_factors.length > 0 && (
                <ul className="space-y-1">
                  {analysis.risk_assessment.risk_factors.map((factor, i) => (
                    <li key={i} className="text-sm text-slate-700 flex items-start gap-2">
                      <span className={`${config.color} mt-0.5`}>•</span>
                      <span>{factor}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Trends */}
            {analysis.trends && analysis.trends.length > 0 && (
              <div className="p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <TrendingUp className="w-4 h-4 text-zinc-900" />
                  <h4 className="font-semibold text-slate-900">Trender</h4>
                </div>
                <ul className="space-y-2">
                  {analysis.trends.map((trend, i) => (
                    <li key={i} className="text-sm text-slate-700 flex items-start gap-2">
                      <span className="text-zinc-900 mt-0.5">→</span>
                      <span>{trend}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Market Insights */}
            {analysis.market_insights && analysis.market_insights.length > 0 && (
              <div className="p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <Lightbulb className="w-4 h-4 text-amber-600" />
                  <h4 className="font-semibold text-slate-900">Markedsinnsikter</h4>
                </div>
                <ul className="space-y-2">
                  {analysis.market_insights.map((insight, i) => (
                    <li key={i} className="text-sm text-slate-700 flex items-start gap-2">
                      <span className="text-amber-600 mt-0.5">💡</span>
                      <span>{insight}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Predictions */}
          {analysis.predictions && analysis.predictions.length > 0 && (
            <div className="p-4 bg-lime-100 rounded-lg border border-lime-200">
              <h4 className="font-semibold text-zinc-900 mb-3">Prediksjoner</h4>
              <ul className="space-y-2">
                {analysis.predictions.map((prediction, i) => (
                  <li key={i} className="text-sm text-slate-700 p-2 bg-white rounded">
                    {prediction}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Recommendations */}
          {analysis.recommendations && analysis.recommendations.length > 0 && (
            <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
              <h4 className="font-semibold text-emerald-900 mb-3">Anbefalinger</h4>
              <ul className="space-y-2">
                {analysis.recommendations.map((rec, i) => (
                  <li key={i} className="text-sm text-slate-700 flex items-start gap-2 p-2 bg-white rounded">
                    <span className="text-emerald-600 mt-0.5">✓</span>
                    <span>{rec}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}